import type { ClientStore } from '../state/clientStore.js';
import { attachEdgeFade } from '../ui/edgeFade.js';
import { createRouteBullet } from './routeBullet.js';
import { VITALS, isCluster } from './vitals.js';
import type { Vital, VitalCluster, VitalDetail, VitalGroup, VitalView } from './vitals.js';
import type { Focus } from './focus.js';
import { isFocusable } from './focus.js';
import { Visibility } from '../hud/visibility.js';
import { attachMobileSheet } from './mobileSheet.js';

const VITAL_VISIBILITY_LAYERS: Readonly<Record<string, string>> = {
  subway: 'subway', buses: 'buses', lirr: 'lirr', mnr: 'mnr', path: 'path',
  ferries: 'ferry', ene: 'ene', bikes: 'citibike', planes: 'flights',
  helicopters: 'flights', flood: 'floodnet', '311': 'c311', grid: 'grid_load',
  power: 'power_outages', harbor: 'harbor',
  speed: 'traffic', weather: 'radar',
};
const VISIBILITY_LABELS: Readonly<Record<string, string>> = {
  subway: 'Subway', buses: 'Buses', lirr: 'LIRR', mnr: 'Metro-North', path: 'PATH',
  ferry: 'Ferry', ene: 'Elevators/Escalators', citibike: 'Citi Bike', flights: 'Flights',
  floodnet: 'Flooding', c311: '311', grid_load: 'Power Cables',
  power_outages: 'Power Outages', harbor: 'Harbor Buoys',
  traffic: 'Traffic', radar: 'Radar',
};

const EYE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="2.5"/></svg>';
const EYE_OFF_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path d="M3 3l18 18M10.6 6.1A10.8 10.8 0 0 1 12 6c6.5 0 10 6 10 6a17 17 0 0 1-2.1 2.8M6.2 6.2C3.5 8 2 12 2 12s3.5 6 10 6c1.2 0 2.3-.2 3.3-.6"/></svg>';

/** Most route bullets to show inline before collapsing the rest into "+N". */
const MAX_BULLETS = 6;

/** Static chevron SVG for cluster chips (trusted constant, never feed-derived). */
const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" ' +
  'stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>';

/** The ordered group names for layout (top-to-bottom desktop, left-to-right mobile). */
export const GROUP_ORDER: VitalGroup[] = ['transit', 'weather', 'city'];

/** Human-readable section headers shown above each group on the desktop panel. */
export const GROUP_LABELS: Record<VitalGroup, string> = {
  transit: 'Transit',
  weather: 'Weather',
  city: 'City',
};

/**
 * One body-mounted tooltip element shared by every `[data-tip]` target in the
 * panel, driven by delegated mouseover/mouseout on the panel root. Body-level
 * because chips are rebuilt on every store update and sit inside overflow-
 * clipped containers (the cluster 0fr→1fr track, the mobile strip) — a fixed
 * element outside the panel survives both. Text is set via textContent only.
 */
export function attachTipLayer(el: HTMLElement): { hideTip: () => void; tipEl: HTMLElement; destroy: () => void } {
  const tipEl = document.createElement('div');
  tipEl.className = 'vital-tip';
  document.body.appendChild(tipEl);
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const hideTip = (): void => {
    if (hideTimer !== null) clearTimeout(hideTimer);
    hideTimer = null;
    tipEl.classList.remove('vital-tip--visible');
  };

  const scheduleHide = (): void => {
    if (hideTimer !== null) clearTimeout(hideTimer);
    hideTimer = setTimeout(hideTip, 100);
  };

  const showTip = (target: HTMLElement): void => {
    if (hideTimer !== null) clearTimeout(hideTimer);
    hideTimer = null;
    const tip = target.getAttribute('data-tip');
    const cardJson = target.getAttribute('data-tip-card');
    let card: { title: string; details: VitalDetail[] } | null = null;
    try {
      card = cardJson ? JSON.parse(cardJson) as { title: string; details: VitalDetail[] } : null;
    } catch {
      return;
    }
    tipEl.replaceChildren();
    tipEl.classList.toggle('vital-tip--card', card !== null);
    if (card) {
      tipEl.appendChild(buildDetailCard(card.title, card.details));
    } else if (tip) {
      tipEl.textContent = tip;
    } else return;
    const rect = target.getBoundingClientRect();
    tipEl.classList.add('vital-tip--visible');
    // Position after making it measurable: beyond the row's right edge so the
    // card leaves the vitals ledger readable, clamped on narrow viewports.
    const tipRect = tipEl.getBoundingClientRect();
    const maxLeft = Math.max(4, window.innerWidth - tipRect.width - 4);
    const left = Math.min(Math.max(4, rect.right + 8), maxLeft);
    tipEl.style.left = `${left}px`;
    // Align the card with the row that opened it so the relationship is
    // immediate without covering the vitals ledger.
    const preferredTop = rect.top;
    const top = Math.min(
      Math.max(4, preferredTop),
      Math.max(4, window.innerHeight - tipRect.height - 4),
    );
    tipEl.style.top = `${top}px`;
  };

  const onMouseOver = (e: MouseEvent): void => {
    const target = (e.target as Element).closest('[data-tip], [data-tip-card]') as HTMLElement | null;
    if (target) showTip(target);
  };

  const onMouseOut = (e: MouseEvent): void => {
    const target = (e.target as Element).closest('[data-tip], [data-tip-card]');
    const related = e.relatedTarget as Node | null;
    if (target && (!related || !target.contains(related))) scheduleHide();
  };

  const onFocusIn = (e: FocusEvent): void => {
    const target = (e.target as Element).closest('[data-tip], [data-tip-card]') as HTMLElement | null;
    if (target) showTip(target);
  };

  const onFocusOut = (e: FocusEvent): void => {
    const target = (e.target as Element).closest('[data-tip], [data-tip-card]');
    if (target) hideTip();
  };

  const onTipMouseEnter = (): void => {
    if (hideTimer !== null) clearTimeout(hideTimer);
    hideTimer = null;
  };
  el.addEventListener('mouseover', onMouseOver);
  el.addEventListener('mouseout', onMouseOut);
  el.addEventListener('focusin', onFocusIn);
  el.addEventListener('focusout', onFocusOut);
  tipEl.addEventListener('mouseenter', onTipMouseEnter);
  tipEl.addEventListener('mouseleave', hideTip);

  return { hideTip, tipEl, destroy: () => {
    hideTip();
    el.removeEventListener('mouseover', onMouseOver);
    el.removeEventListener('mouseout', onMouseOut);
    el.removeEventListener('focusin', onFocusIn);
    el.removeEventListener('focusout', onFocusOut);
    tipEl.removeEventListener('mouseenter', onTipMouseEnter);
    tipEl.removeEventListener('mouseleave', hideTip);
    tipEl.remove();
  } };
}

function buildDetailCard(title: string, details: VitalDetail[]): HTMLElement {
  const card = document.createElement('div');

  const titleEl = document.createElement('div');
  titleEl.className = 'vital-tip-title';
  titleEl.textContent = title;
  card.appendChild(titleEl);

  const body = document.createElement('div');
  body.className = 'vital-tip-body';
  for (const detail of details) {
    if (detail.kind === 'routes') {
      const row = document.createElement('div');
      row.className = 'vital-tip-alert';
      if (detail.routes.length === 0) row.classList.add('vital-tip-alert--no-routes');
      const copy = document.createElement('div');
      copy.className = 'vital-tip-alert-copy';
      copy.textContent = detail.text;
      if (detail.routes.length > 0) {
        const routes = document.createElement('div');
        routes.className = 'vital-tip-routes';
        for (const route of detail.routes) routes.appendChild(createRouteBullet(route));
        row.append(routes, copy);
      } else {
        row.appendChild(copy);
      }
      body.appendChild(row);
    } else if (detail.kind === 'metric') {
      const row = document.createElement('div');
      row.className = 'vital-tip-metric';
      if (detail.elapsed) row.classList.add('vital-tip-metric--elapsed');
      const label = document.createElement('span');
      label.textContent = detail.label;
      const value = document.createElement('strong');
      value.textContent = detail.value;
      row.append(label, value);
      body.appendChild(row);
    } else if (detail.kind === 'alert') {
      const row = document.createElement('div');
      row.className = 'vital-tip-weather-alert';
      const alertTitle = document.createElement('strong');
      alertTitle.textContent = detail.title;
      row.appendChild(alertTitle);
      if (detail.meta) {
        const meta = document.createElement('span');
        meta.textContent = detail.meta;
        row.appendChild(meta);
      }
      body.appendChild(row);
    } else if (detail.kind === 'bar') {
      const row = document.createElement('div');
      row.className = 'vital-tip-bar';
      const heading = document.createElement('div');
      heading.className = 'vital-tip-bar-heading';
      const label = document.createElement('span');
      label.textContent = detail.label;
      const value = document.createElement('strong');
      value.textContent = detail.value;
      heading.append(label, value);
      const track = document.createElement('div');
      track.className = 'vital-tip-bar-track';
      const fill = document.createElement('span');
      fill.className = 'vital-tip-bar-fill';
      fill.style.width = `${Math.max(0, Math.min(1, detail.frac)) * 100}%`;
      track.appendChild(fill);
      row.append(heading, track);
      body.appendChild(row);
    } else if (detail.kind === 'category') {
      const row = document.createElement('div');
      row.className = 'vital-tip-category';
      const label = document.createElement('span');
      label.className = 'vital-tip-category-label';
      label.textContent = detail.label;
      const value = document.createElement('strong');
      value.textContent = detail.value;
      const percent = document.createElement('span');
      percent.className = 'vital-tip-category-percent';
      percent.textContent = detail.percent;
      const track = document.createElement('span');
      track.className = 'vital-tip-category-track';
      const fill = document.createElement('span');
      fill.style.width = `${Math.max(0, Math.min(1, detail.frac)) * 100}%`;
      if (detail.color) fill.style.background = detail.color;
      track.appendChild(fill);
      row.append(label, value, percent, track);
      body.appendChild(row);
    } else if (detail.kind === 'note') {
      const note = document.createElement('div');
      note.className = 'vital-tip-note';
      note.textContent = detail.text;
      body.appendChild(note);
    }
  }
  card.appendChild(body);
  return card;
}

function detailLabel(title: string, details: VitalDetail[]): string {
  const rows = details.map((detail) => {
    if (detail.kind === 'routes') {
      return detail.routes.length > 0 ? `${detail.routes.join(', ')}: ${detail.text}` : detail.text;
    }
    if (detail.kind === 'metric') return `${detail.label}: ${detail.value}`;
    if (detail.kind === 'alert') return [detail.title, detail.meta].filter(Boolean).join(', ');
    if (detail.kind === 'bar') return `${detail.label}: ${detail.value}`;
    if (detail.kind === 'category') return `${detail.label}: ${detail.value}, ${detail.percent}`;
    return detail.text;
  });
  return `${title}. ${rows.join('. ')}`;
}

/**
 * Append a small ⚠ marker inside the chip's label span when the vital's feed
 * is degraded. `data-tip` plugs into the panel's shared tooltip layer;
 * aria-label keeps it readable for assistive tech. Only called when a real
 * LayerState exists — pre-connect "down" views (state undefined) never warn,
 * so the board doesn't flash warnings while connecting.
 */
function appendWarnMarker(labEl: HTMLElement, tip: string): void {
  const warn = document.createElement('span');
  warn.className = 'vital-warn';
  warn.textContent = '⚠';
  warn.setAttribute('data-tip', tip);
  warn.setAttribute('aria-label', tip);
  labEl.appendChild(warn);
}

/** Tooltip copy for a degraded vital: down beats stale. */
export function warnTip(view: VitalView): string {
  return view.down ? `${view.label} — no live data` : `${view.label} — data delayed`;
}

/** A horizontal row of MTA bullets, overflowing into a "+N" chip. */
function buildBulletRow(routes: string[]): HTMLElement {
  const row = document.createElement('div');
  row.className = 'vital-bullets';

  for (const route of routes.slice(0, MAX_BULLETS)) {
    row.appendChild(createRouteBullet(route));
  }
  if (routes.length > MAX_BULLETS) {
    const more = document.createElement('span');
    more.className = 'route-more';
    more.textContent = `+${routes.length - MAX_BULLETS}`;
    row.appendChild(more);
  }
  return row;
}

/**
 * Build one vital chip using createElement + textContent (XSS-safe).
 * The static SVG icon is injected via innerHTML — it is a hardcoded constant,
 * never feed-derived, so this is safe by construction. All other content
 * (value, label) uses textContent exclusively.
 *
 * Children are flat (icon, num, label, [bullets]) so the same DOM reflows two
 * ways: a vertical ledger row on desktop and a stacked chip in the mobile strip,
 * driven entirely by CSS grid placement.
 */
export function buildChip(
  vital: Vital,
  view: VitalView,
  focusedLayerId: string | null,
  warn = false,
  visibility?: Visibility,
  isVisibilityAvailable: (id: string) => boolean = () => true,
): HTMLElement {
  const chip = document.createElement('div');
  chip.className = 'vital-chip';
  chip.dataset.vitalId = vital.id;
  if (view.details && view.details.length > 0) {
    const title = view.detailTitle ?? view.label;
    chip.setAttribute('data-tip-card', JSON.stringify({ title, details: view.details }));
    chip.setAttribute('aria-label', `${view.value} ${view.label}. ${detailLabel(title, view.details)}`);
    chip.setAttribute('tabindex', '0');
  }
  if (view.down) chip.classList.add('down');
  if (view.stale) chip.classList.add('stale');
  if (view.tone) chip.classList.add(view.tone);
  if (view.bullets && view.bullets.length > 0) chip.classList.add('vital-chip--delay');

  // Focusable chips get interactive attributes for click/keyboard handlers
  const focusable = isFocusable(vital.layerId);
  const visibilityLayer = VITAL_VISIBILITY_LAYERS[vital.id];
  const focusLayer = visibilityLayer === 'flights' ? 'flights' : vital.layerId;
  if (focusable) {
    chip.classList.add('focusable');
    chip.setAttribute('role', 'group');
    chip.removeAttribute('tabindex');
    if (!chip.hasAttribute('aria-label')) chip.setAttribute('aria-label', view.label);
  }

  // Selected highlight
  if (focusedLayerId === focusLayer) {
    chip.classList.add('selected');
  }

  const content = focusable ? document.createElement('button') : chip;
  if (focusable) {
    (content as HTMLButtonElement).type = 'button';
    content.className = 'vital-focus';
    content.classList.add('focusable');
    content.classList.toggle('selected', focusedLayerId === focusLayer);
    content.dataset.vitalLayer = focusLayer;
    content.setAttribute('aria-pressed', String(focusedLayerId === focusLayer));
    const detailContext = view.details && view.details.length > 0
      ? `. ${detailLabel(view.detailTitle ?? view.label, view.details)}`
      : '';
    content.setAttribute('aria-label', `Focus ${view.label} layer. ${view.value} ${view.label}${detailContext}`);
    chip.appendChild(content);
  }

  // Icon — static SVG constant, innerHTML is safe here (see module-level comment in icons.ts)
  const iconEl = document.createElement('span');
  iconEl.className = 'vital-icon';
  iconEl.innerHTML = vital.icon;
  content.appendChild(iconEl);

  const numEl = document.createElement('span');
  numEl.className = 'vital-num';
  numEl.dataset.value = view.value;
  numEl.textContent = view.value;
  content.appendChild(numEl);

  const labEl = document.createElement('span');
  labEl.className = 'vital-label';
  labEl.textContent = view.label;
  if (warn) appendWarnMarker(labEl, warnTip(view));
  content.appendChild(labEl);

  if (view.bullets && view.bullets.length > 0) {
    content.appendChild(buildBulletRow(view.bullets));
  }

  if (visibilityLayer && visibility && isVisibilityAvailable(visibilityLayer)) {
    if (!focusable) chip.classList.add('vital-chip--visibility');
    const visible = visibility.isVisible(visibilityLayer);
    chip.classList.toggle('vital-chip--hidden', !visible);
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'vital-visibility';
    toggle.dataset.visibilityLayer = visibilityLayer;
    toggle.setAttribute('aria-pressed', String(visible));
    toggle.setAttribute('aria-label', `${visible ? 'Hide' : 'Show'} ${VISIBILITY_LABELS[visibilityLayer] ?? view.label} layer`);
    toggle.innerHTML = visible ? EYE_SVG : EYE_OFF_SVG;
    chip.appendChild(toggle);
  }

  return chip;
}

/**
 * Build a cluster's DOM unit: a layout-transparent wrapper (display: contents)
 * holding the aggregate chip and its child chips. The wrapper is one element
 * per registry entry so the render loop's replace/append logic treats clusters
 * exactly like plain chips.
 *
 * Children are always mounted inside a `.vital-cluster-children` track so
 * expand/collapse can animate in CSS (0fr→1fr grid track); collapsed children
 * are hidden via `visibility`, which also removes them from the tab order.
 */
export function buildClusterUnit(
  cluster: VitalCluster,
  view: VitalView,
  childViews: VitalView[],
  childWarns: boolean[],
  clusterTip: string | null,
  expanded: boolean,
  focusedLayerId: string | null,
  visibility?: Visibility,
  isVisibilityAvailable: (id: string) => boolean = () => true,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'vital-cluster';
  if (expanded) wrapper.classList.add('vital-cluster--expanded');

  const chip = document.createElement('div');
  chip.className = 'vital-chip vital-chip--cluster';
  if (view.down) chip.classList.add('down');
  if (view.stale) chip.classList.add('stale');
  if (view.tone) chip.classList.add(view.tone);
  chip.setAttribute('role', 'button');
  chip.setAttribute('tabindex', '0');
  chip.setAttribute('aria-expanded', String(expanded));
  chip.dataset.vitalCluster = cluster.id;

  const iconEl = document.createElement('span');
  iconEl.className = 'vital-icon';
  iconEl.innerHTML = cluster.icon; // static SVG constant — safe (see icons.ts)
  chip.appendChild(iconEl);

  const numEl = document.createElement('span');
  numEl.className = 'vital-num';
  numEl.dataset.value = view.value;
  numEl.textContent = view.value;
  chip.appendChild(numEl);

  // Chevron leads the label so the label keeps the last grid column and stays
  // flush with the right-aligned labels of plain chips.
  const chevronEl = document.createElement('span');
  chevronEl.className = 'vital-chevron';
  chevronEl.innerHTML = CHEVRON_SVG; // static constant — safe
  chip.appendChild(chevronEl);

  const labEl = document.createElement('span');
  labEl.className = 'vital-label';
  labEl.textContent = view.label;
  if (clusterTip !== null) appendWarnMarker(labEl, clusterTip);
  chip.appendChild(labEl);

  wrapper.appendChild(chip);

  const childrenEl = document.createElement('div');
  childrenEl.className = 'vital-cluster-children';
  const innerEl = document.createElement('div');
  innerEl.className = 'vital-cluster-children-inner';
  cluster.children.forEach((child, i) => {
    const childChip = buildChip(child, childViews[i]!, focusedLayerId, childWarns[i], visibility, isVisibilityAvailable);
    childChip.classList.add('vital-chip--child');
    innerEl.appendChild(childChip);
  });
  childrenEl.appendChild(innerEl);
  wrapper.appendChild(childrenEl);

  return wrapper;
}

/** Format age in seconds as a human-readable string for the LIVE indicator. */
export function formatLiveAge(ageMs: number): string {
  const s = Math.max(0, Math.round(ageMs / 1000));
  if (s < 60) return `updated ${s}s ago`;
  const m = Math.round(s / 60);
  return `updated ${m}m ago`;
}

/** Read the leading displayed number, tolerating grouping separators and units. */
function displayedNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const match = value.replaceAll(',', '').match(/^[\s]*([+-]?(?:\d+(?:\.\d*)?|\.\d+))/);
  if (!match) return null;
  const number = Number(match[1]);
  return Number.isFinite(number) ? number : null;
}

type NumberSegment =
  | { kind: 'stable'; text: string }
  | { kind: 'changed'; previous: string; next: string };

function changedTextSegments(previousValue: string, nextValue: string): NumberSegment[] {
  let prefixLength = 0;
  while (
    prefixLength < previousValue.length
    && prefixLength < nextValue.length
    && previousValue[prefixLength] === nextValue[prefixLength]
  ) prefixLength += 1;

  let suffixLength = 0;
  while (
    suffixLength < previousValue.length - prefixLength
    && suffixLength < nextValue.length - prefixLength
    && previousValue[previousValue.length - 1 - suffixLength]
      === nextValue[nextValue.length - 1 - suffixLength]
  ) suffixLength += 1;

  const segments: NumberSegment[] = [];
  const prefix = nextValue.slice(0, prefixLength);
  if (prefix) segments.push({ kind: 'stable', text: prefix });

  const previousMiddle = previousValue.slice(prefixLength, previousValue.length - suffixLength);
  const nextMiddle = nextValue.slice(prefixLength, nextValue.length - suffixLength);
  if (previousMiddle.length !== nextMiddle.length) {
    segments.push({ kind: 'changed', previous: previousMiddle, next: nextMiddle });
  } else {
    let runStart = 0;
    while (runStart < nextMiddle.length) {
      const stable = previousMiddle[runStart] === nextMiddle[runStart];
      let runEnd = runStart + 1;
      while (
        runEnd < nextMiddle.length
        && (previousMiddle[runEnd] === nextMiddle[runEnd]) === stable
      ) runEnd += 1;
      segments.push(stable
        ? { kind: 'stable', text: nextMiddle.slice(runStart, runEnd) }
        : {
            kind: 'changed',
            previous: previousMiddle.slice(runStart, runEnd),
            next: nextMiddle.slice(runStart, runEnd),
          });
      runStart = runEnd;
    }
  }

  const suffix = suffixLength === 0 ? '' : nextValue.slice(-suffixLength);
  if (suffix) segments.push({ kind: 'stable', text: suffix });
  return segments;
}

/** Set up the old value as a CSS overlay so the new value can roll into its place. */
function animateNumberChange(
  numberEl: HTMLElement,
  previousValue: string | null | undefined,
  nextValue: string | null | undefined,
): void {
  const previousNumber = displayedNumber(previousValue);
  const nextNumber = displayedNumber(nextValue);
  if (previousNumber === null || nextNumber === null || previousNumber === nextNumber) return;

  const previousText = previousValue ?? '';
  const nextText = nextValue ?? '';
  const segments = changedTextSegments(previousText, nextText);
  numberEl.dataset.previousValue = previousText;
  numberEl.replaceChildren();
  let firstCurrentEl: HTMLElement | null = null;
  for (const segment of segments) {
    if (segment.kind === 'stable') {
      const stableEl = document.createElement('span');
      stableEl.className = 'vital-num-stable';
      stableEl.textContent = segment.text;
      numberEl.appendChild(stableEl);
      continue;
    }

    const rollEl = document.createElement('span');
    rollEl.className = 'vital-num-roll';
    const previousEl = document.createElement('span');
    previousEl.className = 'vital-num-previous';
    previousEl.textContent = segment.previous;
    previousEl.setAttribute('aria-hidden', 'true');
    const currentEl = document.createElement('span');
    currentEl.className = 'vital-num-current';
    currentEl.textContent = segment.next;
    firstCurrentEl ??= currentEl;
    rollEl.append(previousEl, currentEl);
    numberEl.appendChild(rollEl);
  }
  numberEl.classList.add(
    nextNumber > previousNumber ? 'vital-num--roll-up' : 'vital-num--roll-down',
  );
  firstCurrentEl?.addEventListener('animationend', () => {
    numberEl.replaceChildren(nextText);
    numberEl.classList.remove('vital-num--roll-up', 'vital-num--roll-down');
    delete numberEl.dataset.previousValue;
  }, { once: true });
}

export function animateChipChange(existing: HTMLElement, next: HTMLElement): void {
  const previousValue = existing.querySelector<HTMLElement>('.vital-num')?.dataset.value;
  const nextNumberEl = next.querySelector<HTMLElement>('.vital-num');
  const nextValue = nextNumberEl?.dataset.value;
  if (previousValue === nextValue) return;

  next.classList.add('vital-chip--updated');
  if (nextNumberEl) animateNumberChange(nextNumberEl, previousValue, nextValue);
}

/**
 * Render the full vitals board into `el`.
 * Desktop: a vertical panel — LIVE header, grouped sections, then a controls
 * footer. Mobile: the same DOM reflows into a horizontal bottom strip.
 *
 * Returns a `controlsSlot` element (the footer) for the caller to mount the map
 * controls into, so the panel stays decoupled from the map/visibility/theme deps.
 */
